import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  inject,
  signal,
} from "@angular/core";
import {
  FormularLookupDto,
  FormularKategorie,
  EnumNameValueDto,
  FormulareClientProvider,
  FormularDetailDto,
  PdfResult,
  FormularPdfsGetQueryIncludes,
  FormularExtractedDataListGetQueryIncludes,
  AnlagenClientProvider,
  FormularExtractedDataEntryType,
  FacilityMasterDataUpdateCommand,
  AddressesClientProvider,
  AddressPersonLookupDto,
  MailClientProvider,
  MailTemplateGetCommand,
  MailTemplateType,
  MailTemplateDto,
} from "src/app/web-api/web-api";
import { FileHelper } from "src/app/helpers";
import { UserService } from "src/app/services/user.service";
import { CstResponsivePopupService } from "src/app/common-components/resp-popup/services/resp-popup.service";
import {
  InfoportalDocumentCreateComponent,
  IInfoportalDocumentCreateData,
} from "src/app/infoportal-api/components/documents/insert/document-create.component";
import { CstDialogsService } from "src/app/common-components/dialogs/cst-dialogs.service";
import {
  CstListViewDataSource,
  CstListViewRowStyle,
  ICstGuiCommand,
  ICstListViewColumn,
  ICstListViewState,
} from "src/app/common-components/list-view/models";
import { catchError, finalize, map, switchMap, tap } from "rxjs/operators";
import { FormBuilder, FormGroup } from "@angular/forms";
import { EMPTY, Observable, of } from "rxjs";
import { AnlageSearchComponent } from "src/app/anlage-api/components/search/anlage-search.component";
import { AnlageViewModel } from "src/app/planung-api/view-models";
import { CstDialogResult } from "src/app/common-components/dialogs/cst-dialog-enums";
import { AlertService } from "src/app/services";
import { CstMailingService } from "src/app/mailing/mailing.service";
import { ICstMailingSendDailogData } from "src/app/mailing/dialogs/send/send-dialog.component";
import { BFF_IsSaasInstance } from "src/app/injection-tokens/backend-feature-flags";

class FormularTableItem {
  public id = "";
  public category = "";
  public name = "";
  public description = "";
  public changed: Date = null;
  public hasPdfs = false;
  public pdfs = signal<PdfResult[] | null>(null);
  public pdfsExpanded = false;
  public isArchived = false;
  public dto: FormularLookupDto | FormularDetailDto = null;
  public isLoadingPdfs = signal<boolean>(false);
  public isDetailOpenedAtLeastOnce = false;
}

@Component({
  selector: "app-task-formular-table",
  templateUrl: "./task-formular-table.component.html",
  styleUrls: ["./task-formular-table.component.scss"],
  standalone: false,
})
export class TaskFormularTableComponent implements OnInit {
  private _mailClient = inject(MailClientProvider);
  private _formularClient = inject(FormulareClientProvider);
  private _addressClient = inject(AddressesClientProvider);
  private _anlageClient = inject(AnlagenClientProvider);
  private _userService = inject(UserService);
  private _respPop = inject(CstResponsivePopupService);
  private _dlgs = inject(CstDialogsService);
  private _alrt = inject(AlertService);
  private _mailing = inject(CstMailingService);
  isSaasInstance = inject(BFF_IsSaasInstance);

  private _facilityID: number = null;
  private _facilityIDInitialised = false;
  private _formularCategories: EnumNameValueDto[] = null;

  private _facilityNoInternal = "";
  private _facilityNoExternal = "";
  private _facilityLocation = "";
  private _facilityContactMail = "";

  @Input() taskID = "";
  @Input() set facilityID(value: number) {
    this._facilityID = value;
    this._facilityIDInitialised = true;
    if (this._facilityID) {
      const sub = this._anlageClient
        .getSingle(this._facilityID)
        .subscribe((result) => {
          sub.unsubscribe();
          this._facilityNoInternal = result.anlageNrIntern;
          this._facilityNoExternal = result.anlageNrExtern;
          this._facilityLocation = result.standort;
          this._facilityContactMail = result.contactMail;
        });
    }
  }
  get facilityID() {
    return this._facilityID;
  }
  @Output() add = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() change = new EventEmitter<FormularLookupDto | FormularDetailDto>();
  @Output() delete = new EventEmitter<FormularLookupDto | FormularDetailDto>();

  @Output() editFormular = new EventEmitter<{
    status: boolean;
    formularId: string | null;
  }>();

  public dataSource = new CstListViewDataSource<FormularTableItem>({
    sorting: { column: "category", direction: "asc" },
  });
  public columns: ICstListViewColumn<FormularTableItem>[] = [
    {
      header: $localize`:@@PlanungAPI_TaskFormulars_Header_Category:Category`,
      field: "category",
      overflow: "ellipsis",
    },
    {
      header: $localize`:@@PlanungAPI_TaskFormulars_Header_Name:Name`,
      field: "name",
      overflow: "ellipsis",
      command: {
        execute: (item) => this.OnChange(item.dto),
        canExecute: (_) => this.change.observed,
      },
    },
    {
      header: $localize`:@@PlanungAPI_TaskFormulars_Header_Description:Description`,
      field: "description",
      overflow: "ellipsis",
    },
    {
      header: $localize`:@@PlanungAPI_TaskFormulars_Header_Changed:Changed`,
      field: "changed",
      overflow: "ellipsis",
      format: "datetime",
    },
  ];
  // 123456789!myPassword
  public mainActions: ICstGuiCommand[] = null;
  public formFilter: FormGroup = null;

  public isLoadingPdfPerMail = false;

  constructor() {
    const fb = inject(FormBuilder);

    this.dataSource.registerFetchDataCallback(this._onFetchData.bind(this));
    this.formFilter = fb.group({
      showArchived: [false],
    });
    this.formFilter.valueChanges.subscribe((_) => this.dataSource.refresh());
  }

  ngOnInit(): void {
    this._formularClient.getFormularCategories().subscribe((result) => {
      this._formularCategories = result.namesValues;
      this.dataSource.refresh();
    });
    const actions: ICstGuiCommand<FormularTableItem>[] = [];

    actions.push({
      execute: (item: FormularTableItem) => this.onEditFormular(item),
      canExecute: (_) => true,
      label: $localize`:@@PlanungAPI_TaskFormulars_Actions_EditForm:Edit`,
      tooltip: $localize`:@@PlanungAPI_TaskFormulars_Actions_EditForm_Tooltip:Edit this formular`,
      icon: "edit",
    });

    actions.push({
      execute: (item: FormularTableItem) =>
        this.onFacilityMasterDataImport(item),
      canExecute: (item: FormularTableItem) =>
        item.hasPdfs &&
        item.category ===
          this.GetKategorieLabel(FormularKategorie.Datenaufnahme),
      label: $localize`:@@PlanungAPI_TaskFormulars_Actions_ImportFacilityMasterData:Import facility master data`,
      tooltip: $localize`:@@PlanungAPI_TaskFormulars_Actions_ImportFacilityMasterData_Tooltip:Select a facility into which the facility master data from this formular should be imported`,
      icon: "arrow_back",
    });

    if (this.delete.observed) {
      actions.push({
        execute: (item: FormularTableItem) => this.delete.emit(item.dto),
        canExecute: (item) => !item.isArchived,
        label: $localize`:@@PlanungAPI_TaskFormulars_Actions_Archive:Archive`,
        tooltip: $localize`:@@PlanungAPI_TaskFormulars_Actions_Archive_Tooltip:Archive this formular`,
        icon: "delete",
      });
    }

    this.mainActions = actions;
  }

  public getRowStyle(element: FormularTableItem): CstListViewRowStyle {
    return element.isArchived ? "disabled" : "default";
  }
  public canRowExpand(element: FormularTableItem): boolean {
    return element.hasPdfs;
  }

  public RefreshItem(item: FormularDetailDto) {
    const tableItem: FormularTableItem = {
      id: item.id,
      category: this.GetKategorieLabel(item.kategorie),
      changed: item.changed,
      name: item.bezeichnung,
      description: item.beschreibung,
      hasPdfs: item.pdfs && item.pdfs.length > 0,
      pdfs: signal(item.pdfs),
      pdfsExpanded: false,
      isArchived: item.archivedAtUtc && item.archivedAtUtc <= new Date(),
      dto: item,
      isDetailOpenedAtLeastOnce: false,
      isLoadingPdfs: signal(false),
    };
    this.dataSource.upsert(tableItem, (a, b) => a.dto.id === b.dto.id);
  }

  public GetKategorieLabel(kat: FormularKategorie): string {
    const res = this._formularCategories.find((e) => e.value === kat);
    return res ? res.name : kat.toString();
  }

  public onFacilityMasterDataImport(item: FormularTableItem): void {
    if (
      !item.hasPdfs ||
      item.category !== this.GetKategorieLabel(FormularKategorie.Datenaufnahme)
    ) {
      return;
    }
    this._formularClient
      .getExtractedDataList(
        FormularExtractedDataListGetQueryIncludes.FacilityMasterData,
        [item.id],
      )
      .pipe(
        map(
          (x) =>
            x.data.find(
              (d) =>
                d.entryType ===
                FormularExtractedDataEntryType.FacilityMasterData,
            )?.facilityMasterData,
        ),
        switchMap((masterData) => {
          if (masterData?.categories?.length > 0) {
            return this._respPop
              .open(AnlageSearchComponent, {
                data: { searchText: this._facilityNoInternal },
              })
              .afterClosed()
              .pipe(
                switchMap((facility) => {
                  if (!(facility instanceof AnlageViewModel)) {
                    return EMPTY;
                  }
                  return of({ masterData, facility });
                }),
              );
          }
          return this._dlgs
            .Information(
              $localize`:@@PlanungAPI_TaskFormulars_FacilityMasterDataImport_NoMasterData:This formular does not contain any facility master data.`,
            )
            .pipe(switchMap(() => EMPTY));
        }),
        switchMap(({ masterData, facility }) => {
          return this._dlgs
            .AskYesNo(
              $localize`:@@PlanungAPI_TaskFormulars_FacilityMasterDataImport_ConfirmOverride:Should the facility master data really be overwritten with the data from the form?\r\nThis operation cannot be undone.`,
            )
            .pipe(
              switchMap((result) => {
                if (result !== CstDialogResult.Yes) {
                  return EMPTY;
                }
                return of({ masterData, facility });
              }),
            );
        }),
        map(({ masterData, facility }) => {
          const fields = [];
          for (const cat of masterData.categories) {
            if (cat.fields?.length > 0) {
              fields.push(
                ...cat.fields.map((x) => ({
                  fieldID: x.id,
                  value: x.value || "",
                })),
              );
            }
            if (!cat.groups) {
              continue;
            }
            for (const group of cat.groups) {
              fields.push(
                ...group.fields.map((x) => ({
                  fieldID: x.id,
                  value: x.value || "",
                })),
              );
            }
          }
          return { facilityID: facility.anlageID, entries: fields };
        }),
        switchMap((cmdData) => {
          const cmd = new FacilityMasterDataUpdateCommand();
          cmd.init(cmdData);
          return this._anlageClient.updateFacilityMasterData(cmd);
        }),
        catchError((err) => {
          console.error(err);
          this._alrt.showSnackbar(
            $localize`:@@PlanungAPI_TaskFormulars_FacilityMasterDataImport_ImportError:Import failed`,
            { duration: "short", type: "error" },
          );
          return EMPTY;
        }),
      )
      .subscribe((result) => {
        if (result.success) {
          this._alrt.showSnackbar(
            $localize`:@@PlanungAPI_TaskFormulars_FacilityMasterDataImport_ImportSuccess:Import successful`,
            { duration: "short" },
          );
        } else {
          this._dlgs.Warning(
            $localize`:@@PlanungAPI_TaskFormulars_FacilityMasterDataImport_ImportFinishedWithWarnings:Import finished with warnings.\r\nThe following properties could not be imported and must be entered manually\:` +
              "\r\n - " +
              result.warnings.join("\r\n - "),
          );
        }
      });
  }

  public OnAdd(event: string | symbol) {
    if (this.add.observers.length !== 0) this.add.emit(event);
  }

  public OnChange(data: FormularLookupDto | FormularDetailDto) {
    if (this.change.observers.length !== 0) this.change.emit(data);
  }

  public OnDelete(data: FormularLookupDto | FormularDetailDto) {
    if (this.delete.observers.length !== 0) this.delete.emit(data);
  }

  public OnDisplayPdf(pdf: PdfResult) {
    FileHelper.OpenFile(FileHelper.ConvertBase64ToBlob(pdf.data));
  }
  public OnDownloadPdf(pdf: PdfResult) {
    FileHelper.DownloadFile(
      FileHelper.ConvertBase64ToBlob(pdf.data),
      pdf.filename,
    );
  }
  public IsVisibleUploadPdfToInfoportal() {
    return this._facilityIDInitialised;
  }
  public CanUploadPdfToInfoportal() {
    return this._userService?.CurrentUser?.settings?.system?.internalServices
      ?.infoPortalApiIsLicensed;
  }
  public OnUploadPdfToInfoportal(pdf: PdfResult) {
    if (!this._facilityID && this._facilityIDInitialised) {
      // Zeige diese Nachricht nur, wenn die Facility ID zwar als Input Parameter gesetzt wurde,
      // aber trotzdem immer noch NULL ist.
      this._dlgs
        .Information(
          $localize`:TaskFormularTable_FacilityIdIsEmpty:A facility must be selected first.`,
        )
        .subscribe();
      return;
    }
    this._respPop.open(InfoportalDocumentCreateComponent, {
      data: {
        bezeichnung: pdf.label,
        fileName: pdf.filename,
        fileMimeType: "application/pdf",
        fileData: pdf.data,
        facilityID: this._facilityID,
      } as IInfoportalDocumentCreateData,
    });
  }

  public CanSendPdfPerMail(formular: FormularTableItem, pdf: PdfResult) {
    return formular && pdf?.data?.length > 0;
  }
  public OnSendPdfPerMail(formular: FormularTableItem, pdf: PdfResult) {
    if (!this.CanSendPdfPerMail(formular, pdf)) {
      return;
    }
    this.isLoadingPdfPerMail = true;
    this._GetMailDialogData(formular, pdf)
      .pipe(finalize(() => (this.isLoadingPdfPerMail = false)))
      .subscribe((data) => {
        this._mailing.displaySendMailDialog(data);
      });
  }

  private _GetMailDialogData(
    formular: FormularTableItem,
    pdf: PdfResult,
  ): Observable<ICstMailingSendDailogData> {
    const data = {
      attachments: [{ filename: pdf.filename, data: pdf.data }],
    } as ICstMailingSendDailogData;
    if (!this._facilityID) {
      return of(data);
    }
    const command = new MailTemplateGetCommand();
    command.type = MailTemplateType.FormularSendMail;
    command.tokenData = {
      Anlage: {
        NrIntern: this._facilityNoInternal,
        NrExtern: this._facilityNoExternal,
        Standort: this._facilityLocation,
      },
      Formular: { Name: formular.name, Description: formular.description },
    };
    return this._mailClient.getMailTemplate(command).pipe(
      catchError((_) => of(null as MailTemplateDto)),
      tap((templ) => {
        if (templ) {
          data.subject = templ.subject ?? formular.name;
          data.body = templ.body;
        }
      }),
      switchMap((_) =>
        this._addressClient.getClientIDForFacility(this._facilityID),
      ),
      catchError((_) => of(0)),
      switchMap((addressID) => {
        if (!addressID) {
          return of(null as AddressPersonLookupDto[]);
        }
        const mailActionID = this._GetMailActionForFormular(
          formular.dto.kategorie,
        );
        if (!mailActionID) {
          return of(null as AddressPersonLookupDto[]);
        }
        return this._addressClient
          .getPersonList(addressID, [mailActionID])
          .pipe(catchError((_) => of(null as AddressPersonLookupDto[])));
      }),
      switchMap((persons) => {
        if (!persons) {
          if (this._facilityContactMail) {
            data.to = [this._facilityContactMail];
          }
        } else {
          const emails = persons.filter((x) => x.email).map((x) => x.email);
          if (emails && emails.length) {
            data.to = emails;
          }
        }
        return of(data);
      }),
    );
  }
  private _GetMailActionForFormular(category: FormularKategorie) {
    const settings =
      this._userService?.CurrentUser?.settings?.system?.wawiAPI
        ?.mailingAktionen;
    if (!settings) {
      return 0;
    }
    switch (category) {
      case FormularKategorie.Datenaufnahme:
        return settings.formularDatenaufnahme;
      case FormularKategorie.Wartungsnachweis:
        return settings.formularWartungsnachweis;
      case FormularKategorie.Regiebericht:
        return settings.formularRegiebericht;
      default:
        throw new Error(
          'Formular Category "' + category + '" is not supported yet!',
        );
    }
  }

  public onRowExpanded(item: FormularTableItem) {
    if (!item.hasPdfs || item.pdfs()) {
      return;
    }

    item.isDetailOpenedAtLeastOnce = true;
    item.isLoadingPdfs.set(true);

    this._formularClient
      .getFormularPdfs(FormularPdfsGetQueryIncludes.PdfData, [item.dto.id])
      .pipe(
        finalize(() => {
          item.isLoadingPdfs.set(false);
        }),
      )
      .subscribe((result) => {
        const pdfs = result.data.map(
          (x) =>
            new PdfResult({
              data: x.data,
              filename: x.fileName,
              label: x.label,
            }),
        );

        item.pdfs.set(pdfs);
      });
  }

  public DisplayPdfDetail(formular: any): boolean {
    return formular.pdfs() && formular.pdfs().length;
  }

  private _onFetchData(_state: ICstListViewState) {
    const filter = this.formFilter.value;
    return this._formularClient
      .getList(
        undefined,
        this.taskID,
        undefined,
        undefined,
        filter.showArchived,
        0,
        999999,
      )
      .pipe(
        map((result) => ({
          total: result.total,
          data: result.data.map((x) => this._mapLookupToTableItem(x)),
        })),
      );
  }

  private _mapLookupToTableItem(x: FormularLookupDto): FormularTableItem {
    return {
      id: x.id,
      category: this.GetKategorieLabel(x.kategorie),
      name: x.bezeichnung,
      description: x.beschreibung,
      changed: x.changed,
      hasPdfs: x.hasPdfs,
      pdfs: signal(null),
      pdfsExpanded: false,
      isArchived: x.isArchived,
      dto: x,
      isDetailOpenedAtLeastOnce: false,
      isLoadingPdfs: signal(false),
    };
  }

  public onEditFormular(formular: FormularTableItem) {
    this.editFormular.emit({ status: true, formularId: formular.dto.id });
    console.log({ status: true, formularId: formular.dto.id });
  }
}
